import {observer} from 'mobx-react';
import {useCallback, useEffect, useRef} from 'react';
import {Navigate, Route, Routes, useNavigate, useParams} from 'react-router';
import {useLocation} from 'react-router-dom';
import {adminSettingsPage} from '@pages/dashboard/admin/admin-components/admin-people/admin-member/admin-member-components/admin-settings/admin-settings-page';
import {installCreateGoalPage} from '../pages/create-goal/create-goal-install';
import {WorkspaceAPIController} from '@api/workspaces-api/workspaces-api-controller';
import {WorkspaceAPIRequestImpl} from '@api/workspaces-api/workspaces-api-request';
import {importManager} from '../pages/import-goals/import-manager';
import ViewUserFeedback from '@pages/dashboard/reporting/reporting-pages/admin-analytics/analytics-pages/analytics-feedback/view-user-feedback/view-user-feedback';
import {useNetworkState} from '@hooks/network-indicator';
import MakeReview from '../pages/dashboard/culture-competence/make-review/make-review';
import CreateReviewFrameworks from '@pages/dashboard/admin/admin-components/admin-workspace/workspace-performance/admin-culture-competence/create-review-framework/create-review-frameworks';
import CreateCategories from '@pages/dashboard/admin/admin-components/admin-workspace/workspace-performance/admin-culture-competence/create-review-categories/create-categories';
import {CompanyPDFReport} from '../pages/dashboard/reporting/reporting-pages/reporting-export/pdf-templates/reporting-export-pdf';
import {useStoreContext} from '../store/store-context';
import {RedirectTo} from './create-routes';
import {CheckinsComments} from '@pages/dashboard/checkins/checkins-comments/checkins-comments';
import {CreateAlignment} from '@pages/create-alignment/create-alignment';
import {useUrlQuery} from '@hooks/use-url-query';
import {ManageGroups} from '@pages/dashboard/groups/group-drawer';
import {PreviewReviewCycle} from '@pages/dashboard/admin/admin-components/admin-performance/performance-framework/review-cycle/preview-review-cyle/preview-review-cycle';
import {trackPage, trackTime} from '@utils/mix-panel';
import {ErrorBoundary} from '@pages/error/error-boundary';
import {Insights} from '../pages/dashboard/you/you-components/insights/insights';
import {useDirectOptionHook} from '@pages/dashboard/reporting/reporting-pages/reporting-overview/hooks/direct-options.hook';
import {CreateMeeting} from '@pages/1:1s/create-meetings';
import CreateValues from '@pages/dashboard/admin/admin-components/admin-workspace/workspace-performance/admin-culture-competence/create-values/create-values';
import CreateReviewCycle from '../pages/dashboard/admin/admin-components/admin-performance/performance-framework/review-cycle/create-review-cycle';
import {createDashboardRoutes} from './header';
import {usePostHog} from 'posthog-js/react';
import {CascadeKeyResult} from '@pages/cascade-keyresults/cascade-keyresults';
import {ManagerEditGoal} from '../pages/dashboard/goals/manager-edit-goal/manager-edit-goal';
import CreatePerformanceTemplate from '../pages/dashboard/admin/admin-components/admin-performance/performance-framework/create-template/create-template';
import {installCreateQuestionPage} from '../pages/dashboard/company-legacy/company-components/company-checkins/company-checkins-questions/components/create-questions/create-question.install';
import {ViewUserCheckins} from '@pages/dashboard/checkins/direct_reports/view-user-checkins/view-user-checkins';
import {useWorkspaceUseCase} from '@hooks/workspace-usecase';
import {MeetingNotes} from '@pages/1:1s/meeting-notes';
import {useGCalenderHook} from '@hooks/gcalender';
import {initializePendo} from '@utils/pendo-initialize';
import ScrollToTop from '@utils/scrollToTop';
import {ViewMeetingsPage} from '@pages/1:1s/view-meetings/view-meetings-page';
import {ViewTeamMeetings} from '@pages/1:1s/meetings/view-meeting';
import {useOneTimeFetch} from '@hooks/query-hook';
import {useFirebaseAppListeners} from '@hooks/app-updates';
import {CreateCheckinCyclePage} from '@pages/dashboard/checkins/create-checkin-cycle';
import {MyCheckinFormPage} from '@pages/dashboard/checkins/my_checkins/my_checkin-form-page';
import {CheckinCycleResults} from '@pages/dashboard/checkins/team-management/checkin-cycle-results';
import {ParticipantCheckinDetail} from '@pages/dashboard/checkins/team-management/participant-checkin-detail';
import {canAdministerCheckins} from '@pages/dashboard/checkins/checkins-access';

const LegacyCheckinCycleResultsRedirect = () => {
  const {cycleId} = useParams();
  const {search} = useLocation();
  return (
    <Navigate
      to={`/checkins/team-management/cycles/${cycleId}?tab=responses${
        search ? `&${search.slice(1)}` : ''
      }`}
      replace
    />
  );
};

const LegacyFeedbackActionRedirect = ({
  action,
}: {
  action: 'give' | 'request';
}) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const nextAction =
    params.get('id') && action === 'give'
      ? 'respond'
      : params.get('type') === 'praise'
      ? 'post-praise'
      : action === 'request'
      ? 'request-feedback'
      : 'send-feedback';
  const requestId = params.get('id');
  params.delete('type');
  params.delete('id');
  params.set('action', nextAction);
  if (requestId) params.set('requestId', requestId);

  return (
    <Navigate
      to={`/feedback/for-you?${params.toString()}`}
      state={location.state}
      replace
    />
  );
};

export const createHomeRoutes = () => {
  const {DashboardsRoutes} = createDashboardRoutes();
  const {Page: CreateGoal} = installCreateGoalPage();
  const {Page: CreateQuestion} = installCreateQuestionPage();

  const {Page: ImportManager} = importManager();
  const {Page: AdminSettingsPage} = adminSettingsPage();

  const HomeRoutes = observer(() => {
    const {
      billingsStore: {setNumSeats},
      authStore: {auth, setFeatureLimitStatus},
      integrationStore: {isGapiLoaded},
      checkinsStore: {setUserFilter, userFilter},
    } = useStoreContext();
    const canManageCheckins = canAdministerCheckins(auth.user.role);

    const posthog = usePostHog();
    const navigate = useNavigate();
    const location = useLocation();
    const analyticsInitializedFor = useRef<string | null>(null);

    const {fetchGoogleToken} = useGCalenderHook();

    useEffect(() => {
      if (isGapiLoaded) {
        fetchGoogleToken();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isGapiLoaded]);

    useUrlQuery();

    // handle application offline and online state
    const {handleOnline, handleOffline} = useNetworkState();

    useFirebaseAppListeners();

    const handleVisibilityChange = useCallback(() => {
      if (document.hidden) {
        trackPage('page_exit');
      } else {
        trackPage('page_viewed');
        trackTime('page_exit');
      }
    }, []);

    useEffect(() => {
      if (!auth?.user?.workspace?.id) return;
      if (analyticsInitializedFor.current === auth.user.id) return;

      initializePendo({
        userId: auth.user.id,
        workspaceName: auth.user.workspace.name,
        workspaceId: auth.user.workspace.id,
        firstName: auth.user.firstName,
        lastName: auth.user.lastName,
        email: auth.user.email,
        billingStatus: auth.user.workspace.billing?.status || 'unknown',
        role: auth.user.role,
      });

      posthog?.identify(auth.user.id, {
        email: auth.user.email,
        workspace: auth.user.workspace,
      });
      posthog?.capture('$pageview');
      posthog?.capture('$pageleave');
      posthog?.group('workspace', auth.user.workspace.id);
      analyticsInitializedFor.current = auth.user.id;
    }, [auth.user, posthog]);

    useEffect(() => {
      const onlineListener = () => handleOnline();
      window.addEventListener('online', onlineListener);
      window.addEventListener('offline', handleOffline);
      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        window.removeEventListener('online', onlineListener);
        window.removeEventListener('offline', handleOffline);
        document.removeEventListener(
          'visibilitychange',
          handleVisibilityChange,
        );
      };
    }, [handleOnline, handleOffline, handleVisibilityChange]);

    // fetch workspace limit status
    const fetchFeatureLimitStatus = useCallback(async () => {
      const request = new WorkspaceAPIRequestImpl();
      const controller = new WorkspaceAPIController(request);
      const response: any = await controller.fetchFeatureLimitStatus();

      response && setFeatureLimitStatus(response);
      response && setNumSeats(response.currentStatus.seats);
    }, [setFeatureLimitStatus, setNumSeats]);

    useOneTimeFetch(['featureLimitStatus'], () => fetchFeatureLimitStatus());

    const {directReportsTeamOption} = useDirectOptionHook();

    useEffect(() => {
      setUserFilter({
        ...userFilter,
        selectedTeam: directReportsTeamOption[0],
      });

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [directReportsTeamOption]);

    const {
      isPerformanceActive,
      isEngagementActive,
      isOneOnOneEnabled,
      isCheckinsEnabled,
      isFeedBackEnabled,
    } = useWorkspaceUseCase();

    // Handle redirect logic
    useEffect(() => {
      const queryParams = new URLSearchParams(location.search);
      const redirectPath = queryParams.get('redirect');

      if (redirectPath) {
        if (auth.user) {
          navigate(redirectPath);
        } else {
          localStorage.setItem('redirectPath', redirectPath);
        }
      }
    }, [auth.user, location.search, navigate]);

    useEffect(() => {
      const storedRedirectPath = localStorage.getItem('redirectPath');
      if (storedRedirectPath && auth.user) {
        localStorage.removeItem('redirectPath');
        navigate(storedRedirectPath);
      }
    }, [auth.user, navigate]);

    return (
      <ErrorBoundary>
        <ScrollToTop />
        <Routes>
          <Route path="/*" element={<DashboardsRoutes />} />

          {isPerformanceActive && (
            <>
              <Route path="create-goal/*" element={<CreateGoal />} />
              <Route path="import-goal" element={<ImportManager />} />
              <Route path="create-goal/goal-success" element={<CreateGoal />} />
              <Route path="create-alignment" element={<CreateAlignment />} />

              <Route path="cascade-keyresult" element={<CascadeKeyResult />} />

              <Route path="edit-goal" element={<CreateGoal />} />
              <Route
                path="explore-templates"
                element={<CreateGoal exploreTemplates />}
              />
            </>
          )}

          {isEngagementActive && (
            <>
              {isCheckinsEnabled && (
                <>
                  <Route
                    path="/view-checkins/:checkinsId/:type"
                    element={<ViewUserCheckins />}
                  />
                  <Route path="/create-question" element={<CreateQuestion />} />
                  <Route
                    path="create-checkin-cycle"
                    element={
                      canManageCheckins ? (
                        <CreateCheckinCyclePage />
                      ) : (
                        <Navigate to="/checkins/my-checkins" replace />
                      )
                    }
                  />
                  <Route
                    path="checkins/team-management/submissions/:cycleId/results"
                    element={
                      canManageCheckins ? (
                        <LegacyCheckinCycleResultsRedirect />
                      ) : (
                        <Navigate to="/checkins/my-checkins" replace />
                      )
                    }
                  />
                  <Route
                    path="checkins/team-management/cycles/:cycleId"
                    element={
                      canManageCheckins ? (
                        <CheckinCycleResults />
                      ) : (
                        <Navigate to="/checkins/my-checkins" replace />
                      )
                    }
                  />
                  <Route
                    path="checkins/team-management/cycles/:cycleId/participants/:participantId"
                    element={
                      canManageCheckins ? (
                        <ParticipantCheckinDetail />
                      ) : (
                        <Navigate to="/checkins/my-checkins" replace />
                      )
                    }
                  />
                  <Route
                    path="checkins/team-management/submissions/:checkinsId/direct"
                    element={
                      canManageCheckins ? (
                        <ViewUserCheckins />
                      ) : (
                        <Navigate to="/checkins/my-checkins" replace />
                      )
                    }
                  />
                  <Route path="my-checkin" element={<MyCheckinFormPage />} />
                  <Route
                    path="checkins/my-checkin"
                    element={<MyCheckinFormPage />}
                  />
                </>
              )}
              {isFeedBackEnabled && (
                <>
                  <Route
                    path="request-feedback"
                    element={<LegacyFeedbackActionRedirect action="request" />}
                  />
                  <Route
                    path="write-feedback"
                    element={<LegacyFeedbackActionRedirect action="give" />}
                  />

                  <Route
                    path="view-user-feedback/:id"
                    element={<ViewUserFeedback />}
                  />
                </>
              )}
              {isOneOnOneEnabled && (
                <>
                  <Route path="create-1:1/*" element={<CreateMeeting />} />
                  <Route
                    path="create-meeting-notes/*"
                    element={<MeetingNotes />}
                  />
                  <Route
                    path="view-meetings/:id"
                    element={<ViewMeetingsPage />}
                  />

                  <Route
                    path="view-grouped-meetings"
                    element={<ViewTeamMeetings />}
                  />
                </>
              )}
            </>
          )}

          <Route path="/edit-profile/:id" element={<AdminSettingsPage />} />

          <Route
            path="invite-user/invite-success"
            element={
              <Navigate to="/workspace-settings/members?invite=true" replace />
            }
          />

          <Route
            path="invite-user"
            element={
              <Navigate to="/workspace-settings/members?invite=true" replace />
            }
          />

          <Route
            path="make-review"
            element={
              auth?.user?.workspace?.settings?.ccStatus === 'enabled' ? (
                <MakeReview />
              ) : (
                <RedirectTo redirectTo="/" />
              )
            }
          />

          <Route
            path="make-review-company"
            element={
              auth?.user?.workspace?.settings?.ccStatus === 'enabled' ? (
                <MakeReview tab="company" />
              ) : (
                <RedirectTo redirectTo="/" />
              )
            }
          />

          <Route
            path="create-review-cycle"
            element={
              auth?.user?.workspace?.settings?.ccStatus === 'enabled' ? (
                <CreateReviewCycle />
              ) : (
                <RedirectTo redirectTo="/" />
              )
            }
          />
          <Route
            path="preview-review-cycle"
            element={
              auth?.user?.workspace?.settings?.ccStatus === 'enabled' ? (
                <PreviewReviewCycle />
              ) : (
                <RedirectTo redirectTo="/" />
              )
            }
          />

          <Route
            path="edit-review-cycle/:id"
            element={
              auth?.user?.workspace?.settings?.ccStatus === 'enabled' ? (
                <CreateReviewCycle />
              ) : (
                <RedirectTo redirectTo="/" />
              )
            }
          />

          <Route
            path="create-performance-template"
            element={
              auth?.user?.workspace?.settings?.ccStatus === 'enabled' ? (
                <CreatePerformanceTemplate />
              ) : (
                <RedirectTo redirectTo="/" />
              )
            }
          />

          <Route
            path="create-performance-template/:id"
            element={
              auth?.user?.workspace?.settings?.ccStatus === 'enabled' ? (
                <CreatePerformanceTemplate />
              ) : (
                <RedirectTo redirectTo="/" />
              )
            }
          />

          <Route
            path="create-review-frameworks/:id"
            element={<CreateReviewFrameworks />}
          />
          <Route
            path="create-review-frameworks"
            element={<CreateReviewFrameworks />}
          />
          <Route
            path="create-review-categories"
            element={<CreateCategories />}
          />

          <Route path="create-review-values" element={<CreateValues />} />

          <Route path="company/reports/pdf" element={<CompanyPDFReport />} />

          <Route path="*" element={<Navigate to="/home" replace />} />
        </Routes>
        <Insights />
        <ManagerEditGoal />
        <CheckinsComments />
        <ManageGroups />
      </ErrorBoundary>
    );
  });

  return {
    HomeRoutes,
  };
};
